import { useMutation, useQueryClient } from '@tanstack/react-query'

import { graphql_call as graphql_admin_call } from '../../schemas/server_admin_query'
import { graphql } from '../../schemas/server_admin_query'

import { keys } from './keystore.ts'

export const mutation_create_fund = graphql(`
  mutation create_fund(
    $name: String!
    $starting_share_price: Float!
    $management_fee_percentage: Float!
    $token_symbol: String!
    $published: Boolean!
    $market: String
    $publication_schedule: String!
  ) {
    create_fund(
      name: $name
      starting_share_price: $starting_share_price
      management_fee_percentage: $management_fee_percentage
      token_symbol: $token_symbol
      published: $published
      market: $market
      publication_schedule: $publication_schedule
    ) {
      errors
      fund {
        id
        name
        starting_price_per_share
        management_fee_percentage
        created_at
        updated_at
      }
      feeder {
        id
        name
        symbol
        starting_price_per_share
        published
        market
        created_at
        updated_at
      }
    }
  }
`)

export interface CreateFundInput {
  name: string
  starting_share_price: number
  management_fee_percentage: number
  token_symbol: string
  published: boolean
  market?: string
  publication_schedule: string
}

export interface CreateFundMutationResponse {
  fund: {
    id: string
    name: string
    starting_price_per_share: number
    management_fee_percentage: number
    created_at: string
    updated_at: string
  } | null
  feeder: {
    id: string
    name: string
    symbol: string
    starting_price_per_share: number
    published: boolean
    market: string
    created_at: string
    updated_at: string
  } | null
  errors: string[]
}

export function useCreateFund() {
  const queryClient = useQueryClient()

  return useMutation<CreateFundMutationResponse, Error, CreateFundInput>({
    mutationFn: async (
      input: CreateFundInput,
    ): Promise<CreateFundMutationResponse> => {
      try {
        const response = await graphql_admin_call({
          query: mutation_create_fund,
          variables: {
            name: input.name,
            starting_share_price: input.starting_share_price,
            management_fee_percentage: input.management_fee_percentage,
            token_symbol: input.token_symbol,
            published: input.published,
            market: input.market || 'intl',
            publication_schedule: input.publication_schedule,
          },
        })

        if (!response) throw new Error('Failed to create fund')
        const result = response as CreateFundMutationResponse

        if (result.errors.length > 0) {
          throw new Error(result.errors.join(', '))
        }

        return result
      } catch (error) {
        console.error('Error creating fund:', error)
        throw error
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.admin_funds.all.queryKey,
      })
    },
  })
}
